/**
 * Hosts that apply a TeamDutyProfile (or snapshot) onto a record.
 * Adding a page: model `teams[]`, registry row (defaultSituationKey + assignmentMode),
 * handlers, TeamAssociationCard. Unknown parentType is fail-closed (400).
 */

export const TEAM_TYPE_VALUES = Object.freeze([
    'cmt',
    'imt',
    'recovery',
    'communications',
    'other',
]);

export const TEAM_DUTY_SITUATION_VALUES = Object.freeze([
    'crisis',
    'exercise',
    'other',
]);

export const TEAM_DUTY_SITUATION_CATALOG = Object.freeze({
    crisis: Object.freeze({ key: 'crisis', labelKey: 'situations.crisis' }),
    exercise: Object.freeze({ key: 'exercise', labelKey: 'situations.exercise' }),
    other: Object.freeze({ key: 'other', labelKey: 'situations.other' }),
});

export const TEAM_ASSIGNMENT_MODES = Object.freeze(['reference', 'snapshot']);

export function getTeamDutySituation(key) {
    if (!key) return null;
    return TEAM_DUTY_SITUATION_CATALOG[key] || null;
}

export function isTeamDutySituation(key) {
    return Boolean(getTeamDutySituation(key));
}

export const TEAM_ASSOCIATION_HOST_REGISTRY = Object.freeze({
    Bcp: Object.freeze({
        entityType: 'Bcp',
        teamsField: 'teams',
        pageKey: 'bcmBcp',
        detailPathTemplate: '/bcm/bcp/:id',
        labelKey: 'hosts.bcp',
        titleFields: Object.freeze(['code', 'name']),
        searchFields: Object.freeze(['code', 'name']),
        listApiPath: '/bcp',
        eventPrefix: 'bcp',
        invalidationKey: 'team',
        associationTab: 'roles',
        defaultSituationKey: 'crisis',
        assignmentMode: 'reference',
        copyFrom: Object.freeze([]),
    }),
    CrisisEvent: Object.freeze({
        entityType: 'CrisisEvent',
        teamsField: 'teams',
        pageKey: 'bcmCrisisEvent',
        detailPathTemplate: '/bcm/crisis-event/:id',
        labelKey: 'hosts.crisisEvent',
        titleFields: Object.freeze(['code', 'title']),
        searchFields: Object.freeze(['code', 'title']),
        listApiPath: '/crisis-event',
        eventPrefix: 'crisisEvent',
        invalidationKey: 'team',
        associationTab: 'overview',
        defaultSituationKey: 'crisis',
        assignmentMode: 'snapshot',
        copyFrom: Object.freeze([
            Object.freeze({ sourceEntityType: 'Bcp', targetField: 'activatedBcps' }),
            Object.freeze({
                sourceEntityType: 'Bia',
                targetField: 'relatedBias',
                nestedEntityType: 'Bcp',
                nestedSourceField: 'relatedBcps',
                includeSourceTeams: false,
            }),
        ]),
    }),
    Exercise: Object.freeze({
        entityType: 'Exercise',
        teamsField: 'teams',
        pageKey: 'bcmExercises',
        detailPathTemplate: '/bcm/exercises/:id',
        labelKey: 'hosts.exercise',
        titleFields: Object.freeze(['code', 'name']),
        searchFields: Object.freeze(['code', 'name']),
        listApiPath: '/exercises',
        eventPrefix: 'exercise',
        invalidationKey: 'team',
        associationTab: 'overview',
        defaultSituationKey: 'exercise',
        assignmentMode: 'snapshot',
        copyFrom: Object.freeze([
            Object.freeze({ sourceEntityType: 'Bcp', targetField: 'relatedBcps' }),
        ]),
    }),
});

export const TEAM_ASSOCIATION_HOST_TYPES = Object.freeze(Object.keys(TEAM_ASSOCIATION_HOST_REGISTRY));

export function getTeamAssociationHost(entityType) {
    if (!entityType) return null;
    return TEAM_ASSOCIATION_HOST_REGISTRY[entityType] || null;
}

export function isTeamAssociationHost(entityType) {
    return Boolean(getTeamAssociationHost(entityType));
}

export function resolveTeamHostDetailPath(entityType, id) {
    const host = getTeamAssociationHost(entityType);
    if (!host?.detailPathTemplate || !id) return null;
    return host.detailPathTemplate.replace(':id', String(id));
}

export function listTeamAssociationHosts() {
    return TEAM_ASSOCIATION_HOST_TYPES.map((entityType) => TEAM_ASSOCIATION_HOST_REGISTRY[entityType]);
}

/**
 * Members shown on a card / used for notification assignees.
 * Reference without override reads the catalog profile; snapshot and override read assignment.members.
 */
export function effectiveAssignmentMembers(assignment, profile) {
    if (!assignment) return [];
    if (assignment.assignmentMode === 'snapshot' || assignment.override === true) {
        return assignment.members || [];
    }
    if (assignment.assignmentMode === 'reference') {
        return profile?.members || [];
    }
    return assignment.members?.length ? assignment.members : (profile?.members || []);
}

/**
 * Execution tasks are assignment-owned regardless of assignment mode, because
 * timing and ordering are scheduled per host record. The catalog only seeds them
 * when the assignment has none yet.
 */
export function effectiveAssignmentExecutionTasks(assignment, profile) {
    if (!assignment) return [];
    if (assignment.executionTasks?.length) return assignment.executionTasks;
    return profile?.executionTasks || [];
}
